import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import EasyCrop from 'react-easy-crop';
import ReactToPrint from 'react-to-print';
import html2canvas from 'html2canvas';
import getCroppedImg from '../ImageCropper/CropImage';
import { config } from '../config';

const photoSizes = [

  {
    id: "local-passport",
    name: "Local Passport (1x1.5 inch)",
    height: 128.50393701,
    width: 98.267716535,
  },
  { id: 'passport', name: 'Passport (3.5x4.5 cm)', height: 170, width: 132 }, // approx 35x45mm at 300dpi is actually 413x531, using smaller scaled versions

  {
    id: " local-passport-dual",
    name: "Local Passport Dual (2x1.5 inch)",
    height: 144,
    width: 96 * 2,
  },
  { id: 'stamp', name: 'Stamp (2x2 cm)', height: 75.5, width: 75.5 },
  { id: 'postcard', name: 'Postcard (4x6 in)', height: 384, width: 576 },
  { id: 'postcard-portrait', name: 'Postcard (4x6 in) portrait', height: 576, width: 384 },
  {
    id: "postcard-3x5",
    name: "Post Card (3x5 inch)",
    height: 288,
    width: 480,
  },
  {
    id: "postcard-3.2x4.8",
    name: "Post Card (3.2x4.8 inch)",
    height: 307.2,
    width: 460.8,
  },
  { id: '2x2', name: '2x2 in', height: 192, width: 192 }
];

const paperSizes = [
  { id: 'a4', name: 'A4', width: 210, height: 297 },
  { id: 'a5', name: 'A5', width: 148, height: 210 },
  { id: 'letter', name: 'Letter', width: 215.9, height: 279.4 },
  { id: '4x6', name: '4x6 in', width: 101.6, height: 152.4 },
  { id: '5x7', name: '5x7 in', width: 127, height: 177.8 }
];

export default function Studio() {
  const location = useLocation();
  const passedImage = location.state?.imageSrc;

  // Try to load state from local storage
  const loadState = (key, fallback) => {
    try {
      const stored = localStorage.getItem(`studio_${key}`);
      return stored ? JSON.parse(stored) : fallback;
    } catch {
      return fallback;
    }
  };

  const [imageSrc, setImageSrc] = useState(passedImage || loadState('imageSrc', null));
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [removeBg, setRemoveBg] = useState(loadState('removeBg', true));

  // Crop state
  const [crop, setCrop] = useState(loadState('crop', { x: 0, y: 0 }));
  const [zoom, setZoom] = useState(loadState('zoom', 1));
  const [rotation, setRotation] = useState(loadState('rotation', 0));

  const initialSizeId = loadState('selectedSizeId', photoSizes[0].id);
  const [selectedSize, setSelectedSize] = useState(photoSizes.find(s => s.id === initialSizeId) || photoSizes[0]);
  const [selectedBg, setSelectedBg] = useState(loadState('selectedBg', '#ffffff'));

  // Output state
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(loadState('croppedAreaPixels', null));
  const [finalImage, setFinalImage] = useState(loadState('finalImage', null));

  // Page Setup State
  const initialPaperId = loadState('paperId', paperSizes[0].id);
  const [paper, setPaper] = useState(paperSizes.find(p => p.id === initialPaperId) || paperSizes[0]);
  const [landscape, setLandscape] = useState(loadState('landscape', false));
  const [copies, setCopies] = useState(loadState('copies', 2));
  const [margin, setMargin] = useState(loadState('margin', 5));
  const [spacing, setSpacing] = useState(loadState('spacing', 3));
  const [showGuides, setShowGuides] = useState(loadState('showGuides', false));

  // Persist state
  useEffect(() => {
    localStorage.setItem('studio_imageSrc', JSON.stringify(imageSrc));
    localStorage.setItem('studio_removeBg', JSON.stringify(removeBg));
    localStorage.setItem('studio_crop', JSON.stringify(crop));
    localStorage.setItem('studio_zoom', JSON.stringify(zoom));
    localStorage.setItem('studio_rotation', JSON.stringify(rotation));
    localStorage.setItem('studio_selectedSizeId', JSON.stringify(selectedSize.id));
    localStorage.setItem('studio_selectedBg', JSON.stringify(selectedBg));
    localStorage.setItem('studio_croppedAreaPixels', JSON.stringify(croppedAreaPixels));
    localStorage.setItem('studio_finalImage', JSON.stringify(finalImage));
    localStorage.setItem('studio_paperId', JSON.stringify(paper.id));
    localStorage.setItem('studio_landscape', JSON.stringify(landscape));
    localStorage.setItem('studio_copies', JSON.stringify(copies));
    localStorage.setItem('studio_margin', JSON.stringify(margin));
    localStorage.setItem('studio_spacing', JSON.stringify(spacing));
    localStorage.setItem('studio_showGuides', JSON.stringify(showGuides));
  }, [imageSrc, removeBg, crop, zoom, rotation, selectedSize, selectedBg, croppedAreaPixels, finalImage, paper, landscape, copies, margin, spacing, showGuides]);

  const clearStudio = () => {
    setImageSrc(null);
    setFinalImage(null);
    setZoom(1);
    setRotation(0);
    setCrop({ x: 0, y: 0 });
  };

  const printRef = useRef();

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    setErrorMsg('');

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('removeBg', removeBg);

      const res = await fetch(`${config.SERVER_BASE_URL}/api/photos/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to upload photo');
      }

      const data = await res.json();
      setImageSrc(data.url);

      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        window.dispatchEvent(new Event('userUpdate'));
      }
    } catch (err) {
      setErrorMsg(err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const generatePreview = async () => {
    try {
      if (!imageSrc || !croppedAreaPixels) return;
      const croppedImage = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        selectedBg,
        rotation
      );
      setFinalImage(croppedImage);
      // reset copies to max possible
    } catch (e) {
      console.error(e);
    }
  };

  const deductCredits = async () => {
    try {
      const res = await fetch(`${config.SERVER_BASE_URL}/api/photos/download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (!res.ok) {
        throw new Error('Insufficient credits or server error');
      }
      const data = await res.json();
      if (data.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        window.dispatchEvent(new Event('userUpdate'));
      }
      return true;
    } catch (err) {
      setErrorMsg(err.message);
      return false;
    }
  };

  const handleDownloadPng = async () => {
    const hasCredits = await deductCredits();
    if (!hasCredits) return;

    if (printRef.current) {
      const canvas = await html2canvas(printRef.current, { scale: 2 });
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'photo-page.png';
      link.href = dataUrl;
      link.click();
    }
  };

  // Compute paper dimensions
  const pWidth = landscape ? paper.height : paper.width;
  const pHeight = landscape ? paper.width : paper.height;

  // Approximate pixel sizes for CSS rendering (using 3.78 pixels per mm as a rough guide for screen, though react-to-print scales it)
  const mmToPx = (mm) => mm * 3.779527559;

  // Compute maximum copies
  const maxCols = Math.floor((pWidth - margin * 2 + spacing) / (selectedSize.width / 3.78 + spacing));
  const maxRows = Math.floor((pHeight - margin * 2 + spacing) / (selectedSize.height / 3.78 + spacing));
  const maxPossible = Math.max(1, maxCols * maxRows);

  useEffect(() => {
    if (copies > maxPossible) setCopies(maxPossible);
  }, [paper, landscape, margin, spacing, selectedSize, copies, maxPossible]);

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <header className="px-8 py-6 border-b border-slate-200 bg-white shadow-sm z-10 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Photo Studio</h1>
          <p className="text-slate-500 mt-1">Upload, edit, and prepare your photos for printing</p>
        </div>
        <button onClick={clearStudio} className="px-4 py-2 text-sm text-red-600 border border-red-200 hover:bg-red-50 rounded-lg transition-colors font-medium">
          Clear & Reset
        </button>
      </header>

      {errorMsg && (
        <div className="bg-red-50 text-red-600 px-8 py-3 border-b border-red-100 flex justify-between items-center">
          <span>{errorMsg}</span>
          <button onClick={() => setErrorMsg('')} className="font-bold">×</button>
        </div>
      )}

      <div className="flex-1 p-8 flex gap-8 h-[calc(100vh-100px)] overflow-hidden">
        {/* Main Editor / Preview Area */}
        <div className="flex-1 bg-slate-200/50 rounded-2xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center relative overflow-y-auto">

          {!imageSrc && !isProcessing && (
            <div className="text-center">
              <input
                type="file"
                id="photo-upload"
                className="hidden"
                accept="image/png, image/jpeg"
                onChange={handleFileUpload}
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                  <svg className="w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-slate-800">Upload a Photo</h3>
                <p className="text-slate-500 mt-1 mb-4">PNG, JPG up to 10MB {removeBg ? '(Costs 2 Credits)' : '(Costs 0 Credits)'}</p>
                <div className="mb-4">
                  <label className="flex items-center justify-center gap-2 text-sm text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={removeBg} onChange={(e) => setRemoveBg(e.target.checked)} className="rounded text-blue-600" />
                    Remove Background (AI)
                  </label>
                </div>
                <span className="inline-block px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors shadow-sm shadow-blue-200">
                  Browse Files
                </span>
              </label>
            </div>
          )}

          {isProcessing && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-slate-600">Removing background and preparing image...</p>
            </div>
          )}

          {imageSrc && !finalImage && (
            <div className="absolute inset-0 bg-slate-800" style={{ backgroundColor: selectedBg }}>
              <EasyCrop
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={selectedSize.width / selectedSize.height}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                // onZoomChange={setZoom}
                onRotationChange={setRotation}
              />
            </div>
          )}

          {finalImage && (
            <div className="w-full h-full bg-slate-200 overflow-auto py-8 flex justify-center items-start">
              {/* Paper Canvas */}
              <div
                ref={printRef}
                className="bg-white shadow-md relative mx-auto"
                style={{
                  width: `${pWidth}mm`,
                  minHeight: `${pHeight}mm`,
                  padding: `${margin}mm`,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignContent: 'flex-start',
                  gap: `${spacing}mm`
                }}
              >
                {Array.from({ length: copies }).map((_, i) => (
                  <div key={i} style={{
                    width: `${selectedSize.width / 3.78}mm`,
                    height: `${selectedSize.height / 3.78}mm`,
                    position: 'relative'
                  }}>
                    <img
                      src={finalImage}
                      alt={`Copy ${i}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        border: showGuides ? '0.5px dashed #ccc' : 'none'
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar Controls */}
        <div className="w-80 flex flex-col gap-6 overflow-y-auto pr-2 pb-8 custom-scrollbar">
          {imageSrc && !finalImage && (
            <>
              {/* Size Presets */}
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-4">Photo Size</h3>
                <div className="grid grid-cols-2 gap-3">
                  {photoSizes.map((size) => (
                    <button
                      key={size.id}
                      onClick={() => setSelectedSize(size)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border ${selectedSize.id === size.id ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                    >
                      {size.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Adjustments */}
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-slate-800">Adjustments</h3>
                  <button onClick={() => { setZoom(1); setRotation(0); }} className="text-xs text-blue-600 hover:underline">Reset</button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm text-slate-600 flex justify-between mb-2">
                      Zoom <span>{Math.round(zoom * 100)}%</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setZoom(z => Math.max(1, z - 0.1))} className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg hover:bg-slate-200">-</button>
                      <input type="range" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1" />
                      <button onClick={() => setZoom(z => Math.min(3, z + 0.1))} className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg hover:bg-slate-200">+</button>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-slate-600 flex justify-between mb-2">
                      Rotation <span>{rotation}°</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setRotation(r => r - 1)} className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg hover:bg-slate-200">-</button>
                      <input type="range" min={0} max={360} value={rotation} onChange={(e) => setRotation(Number(e.target.value))} className="flex-1" />
                      <button onClick={() => setRotation(r => r + 1)} className="w-8 h-8 flex items-center justify-center bg-slate-100 rounded-lg hover:bg-slate-200">+</button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Background Color */}
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm">
                <h3 className="font-semibold text-slate-800 mb-4">Background Color</h3>
                <div className="flex gap-3 overflow-x-auto flex-wrap">
                  {["#03a9f4",
                    "#71cdf6",
                    "#2196f3",
                    "#9c27b0",
                    "#FF5733",
                    "#ff9800",
                    "#673ab7",
                    "#3f51b5",
                    "#4caf50",
                    "#8bc34a",

                    "#ffffff",
                    "#e3e3e2",].map((color) => (
                      <button
                        key={color}
                        onClick={() => setSelectedBg(color)}
                        className={`w-10 h-10 rounded-full border-2 flex items-center justify-center ${selectedBg === color ? 'border-slate-800' : 'border-slate-200'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                </div>
              </div>

              <button
                onClick={generatePreview}
                className="w-full py-3 bg-slate-800 hover:bg-slate-900 text-white font-medium rounded-xl transition-colors"
              >
                Page Setup
              </button>
            </>
          )}

          {finalImage && (
            <>
              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-4">
                <h3 className="font-semibold text-slate-800">Page Setup</h3>

                <div>
                  <label className="block text-sm text-slate-600 mb-2">Paper Size</label>
                  <select
                    value={paper.id}
                    onChange={(e) => setPaper(paperSizes.find(p => p.id === e.target.value))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {paperSizes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-2">Orientation</label>
                  <div className="flex gap-2">
                    <button onClick={() => setLandscape(false)} className={`flex-1 py-2 rounded-lg text-sm border ${!landscape ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-slate-200 text-slate-600'}`}>Portrait</button>
                    <button onClick={() => setLandscape(true)} className={`flex-1 py-2 rounded-lg text-sm border ${landscape ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-slate-200 text-slate-600'}`}>Landscape</button>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between text-sm text-slate-600 mb-1">
                    <label>Copies</label>
                    <span className="text-slate-400">Max: {maxPossible}</span>
                  </div>
                  <input
                    type="number" min="1" max={maxPossible}
                    value={copies} onChange={(e) => setCopies(Math.min(maxPossible, Math.max(1, Number(e.target.value))))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Margin (mm)</label>
                    <input type="number" min="0" max="50" value={margin} onChange={(e) => setMargin(Number(e.target.value))} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Spacing (mm)</label>
                    <input type="number" min="0" max="20" value={spacing} onChange={(e) => setSpacing(Number(e.target.value))} className="w-full px-3 py-2 border border-slate-200 rounded-lg" />
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer pt-2">
                  <input type="checkbox" checked={showGuides} onChange={(e) => setShowGuides(e.target.checked)} className="rounded text-blue-600" />
                  Show cut guides
                </label>
              </div>

              <div className="bg-white rounded-2xl p-5 border border-slate-200 shadow-sm space-y-3">
                <h3 className="font-semibold text-slate-800 mb-2">Export (Costs 1 Credit)</h3>

                <ReactToPrint
                  onBeforeGetContent={deductCredits}
                  trigger={() => (
                    <button className="w-full py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-medium rounded-xl transition-colors">
                      Print Page
                    </button>
                  )}
                  content={() => printRef.current}
                />

                <button
                  onClick={handleDownloadPng}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors"
                >
                  Download PNG
                </button>

                <button
                  onClick={() => setFinalImage(null)}
                  className="w-full py-2 text-slate-600 font-medium hover:text-slate-800 transition-colors mt-2"
                >
                  Back to Editor
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
